import React, { useState, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import styles from "./styles.module.css";
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Loader from '../../../components/Loader';
import { CLEVERTAP_EVENTS, NAVIGATION_ROUTES } from '../../../constant';
import { useAuthenticateUserApi } from '../../../api/useAuthApi';
import { useRequestOtpApi } from '../../../api/useRequestOtpApi';
import { dispatchToken, useTokenStore } from '../../../storage/tokenState';
import { setAuthTokenInHeader } from '../../../api';
import { RESEND_OTP_DEBOUNCE_DELAY, RESENT_OTP_TIME } from '../../../appConstants/AppConstant';
import { debounce } from 'lodash';
import { SCREEN_NAMES } from '../../../utils/helpers/constants';
import { SIGN_IN_CODES } from '../../../utils/helpers/constants';
import { getPartnerId } from '../../../utils/helpers/common';
import { initializeGlobalSocket } from '../../../classes/socket/mainSocket/SocketService';
import ExistingZupeeUserModal from '../../../components/ExistingZupeeUserModal';
import { pushClevertapEvent } from '../../../utils/analytics/clevertap';
import { fetchAndStoreUserDetails } from '../../../services/AuthService';
import { ELanguage } from '../../../storage/store';
import {handleClickCTEventSuccess, handleClickCTEventExistingUser, handleClickCTEventError, handleClickCTEventSignUpSuccessNewUser, handleClickCTEventSignUpSuccessExistingUser} from '../../../utils/events/events';

declare global {
  interface CredentialRequestOptions {
    otp?: {
      transport: string[];
    };
  }
  interface Credential {
    code?: string;
  }
}

// Add CleverTap profile push function
const pushCleverTapProfile = (phoneNumber: string, identity: string) => {
  if (typeof window === "undefined") return;

  const clevertap = (window as any).clevertap;
  if (!clevertap?.profile) {
    console.warn("CleverTap is not initialized yet.");
    return;
  }

  try {
    clevertap.onUserLogin.push({
      "Site": {
        "Identity": identity,
        "Phone": `+91${phoneNumber}`,
        "MSG-email": true,
        "MSG-push": true,
        "MSG-sms": true,
        "MSG-whatsapp": true,
      }
    })
    console.log(`%cCleverTap Profile Pushed for: ${phoneNumber}`, "color:green;font-weight:bold");
  } catch (error) {
    console.error("%cFailed to push CleverTap profile:", "color:red;font-weight:bold", error);
  }
};

// Update your useAutoReadSMS hook
const useAutoReadSMS = (setOtp: React.Dispatch<React.SetStateAction<string[]>>) => {
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const handleAutoRead = async () => {
      if ('OTPCredential' in window) {
        abortControllerRef.current = new AbortController();

        try {
          const credential = await navigator.credentials.get({
            otp: { transport: ['sms'] },
            signal: abortControllerRef.current?.signal,
          });

          if (credential && 'code' in credential && credential.code) {
            const code = credential.code;
            if (code.length === 6) {
              setOtp(code.split(''));
              const form = document.querySelector('form');
              if (form) form.submit();
            }
          }
        } catch (err) {
          console.log('WebOTP error:', err);
        }
      }

      // Fallback for Android Chrome and other browsers
      const handleMessage = (event: MessageEvent) => {
        if (event.data?.type === 'sms' && event.data?.message) {
          const otpMatch = event.data.message.match(/\b\d{6}\b/);
          if (otpMatch) {
            const code = otpMatch[0];
            setOtp(code.split(''));
          }
        }
      };

      window.addEventListener('message', handleMessage);
      return () => {
        window.removeEventListener('message', handleMessage);
        abortControllerRef.current?.abort();
      };
    };

    handleAutoRead();
  }, [setOtp]);
};

const OtpScreen: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { state } = location;
  const { screenName = SCREEN_NAMES.SIGNUP } = state || {};
  // Where to land after a successful login. SignIn forwards its location state
  // here untouched, so callers set this when they need the user back on the
  // screen that sent them to log in. Defaults to the landing page (WALLET);
  // callers wanting the lobby must pass returnTo explicitly.
  const { returnTo, autoWithdraw } = state || {};
  const postLoginRoute = returnTo || NAVIGATION_ROUTES.WALLET;
  const [otp, setOtp] = useState<string[]>(Array(6).fill(''));
  const navigate = useNavigate();
  const { phoneNumber, action } = useParams<{ phoneNumber: string; action: string }>();
  const { language = 'en' } = useTokenStore();
  const inputRefOtp = useRef<(HTMLInputElement | null)[]>([]);
  const [countdown, setCountdown] = useState<number>(RESENT_OTP_TIME);
  const [isOtpSent, setIsOtpSent] = useState<boolean>(true);
  const [isExistingUser, setIsExistingUser] = useState<boolean>(false);
  const [isCrossLoading, setIsCrossLoading] = useState<boolean>(false);
  const authContentBoxRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);


  useAutoReadSMS(setOtp);

  useAutoReadSMS(setOtp);

  // API hooks
  const { refetch, isLoading, error } = useAuthenticateUserApi(
    phoneNumber as string,
    action as string,
    language,
    true,
    otp.join(''),
    getPartnerId(),
    screenName
  );

  const { refetchOTP } = useRequestOtpApi(phoneNumber as string, action as string, true, language);

  useEffect(() => {
    inputRefOtp.current = Array(6).fill(null).map((_, i) => inputRefOtp.current[i] || null);

    setTimeout(() => {
      const firstInput = inputRefOtp.current[0];
      if (firstInput) {
        firstInput.focus();
        firstInput.click();
      }
    }, 300);
  }, []);


  useEffect(() => {
    // Handle keyboard and focus
    const handleResize = () => {
      const viewportHeight = window.visualViewport?.height || window.innerHeight;
      const windowHeight = window.innerHeight;

      if (viewportHeight < windowHeight && buttonRef.current) {
        // Keyboard is open, scroll button into view
        buttonRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
      }
    };

    // Listen to viewport changes
    window.visualViewport?.addEventListener('resize', handleResize);
    window.addEventListener('resize', handleResize);

    // Focus first input after a short delay
    const focusTimer = setTimeout(() => {
      const firstInput = inputRefOtp.current[0];
      if (firstInput) {
        firstInput.focus();
      }
    }, 300);

    return () => {
      window.visualViewport?.removeEventListener('resize', handleResize);
      window.removeEventListener('resize', handleResize);
      clearTimeout(focusTimer);
    };
  }, []);

  useEffect(() => {
    if (countdown > 0 && isOtpSent) {
      const timerId = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timerId);
    }
    if (countdown === 0) setIsOtpSent(false);
  }, [countdown, isOtpSent]);

  // Clevertap Event
  useEffect(() => {
    const trackingTimeout = setTimeout(() => {
      pushClevertapEvent(CLEVERTAP_EVENTS.EVENT_PWA_OTP_SCREEN);
    }, 100);
    return () => clearTimeout(trackingTimeout);
  }, []);

  const handleInputChange = (value: string, index: number) => {
    if (!/^[0-9]*$/.test(value)) return;
    const newOtp = [...otp];

    if (value.length > 1) {
      for (let i = 0; i < value.length && index + i < newOtp.length; i++) {
        newOtp[index + i] = value[i];
      }
    } else {
      newOtp[index] = value;
    }
    setOtp(newOtp);

    const nextIndex = index + (value.length > 1 ? value.length - 1 : 1);
    if (nextIndex < newOtp.length) {
      setTimeout(() => {
        inputRefOtp.current[nextIndex]?.focus();
      }, 0);
    }
  };

  // Add this function to handle paste events
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>, index: number) => {
    e.preventDefault(); // Prevent the default paste behavior
    const pastedData = e.clipboardData.getData('Text'); // Get the pasted text
    handleInputChange(pastedData, index); // Call the input change handler
  };

  const handleBackspace = (e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Backspace') {
      const newOtp = [...otp];
      if (otp[index] === '' && index > 0) {
        inputRefOtp.current[index - 1]?.focus();
      } else {
        newOtp[index] = '';
        setOtp(newOtp);
      }
    }
  };

  const handleResendOtp = async () => {
    try {
      setIsOtpSent(true);
      setCountdown(RESENT_OTP_TIME);
      await refetchOTP();
      toast.success(t("otpResent"));
    } catch (err) {
      toast.error(t("otpResentFailed"));
    }
  };
  const throttledResendOtp = useCallback(debounce(handleResendOtp, RESEND_OTP_DEBOUNCE_DELAY), []);


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const response = await refetch();
      const authResponse = response?.data;
      if (authResponse?.success && authResponse?.flag === true &&
        authResponse?.errorCode === SIGN_IN_CODES.SUCCESS_CODE) {
        const accessToken = response?.data?.signUpData?.accessToken || '';
        const tokenData = response?.data?.signUpData || {}
        const _id = response?.data?.signUpData?._id || '';
        const postSignupConfig = response?.data?.postConfig || {};
        setAuthTokenInHeader(accessToken, _id);
        dispatchToken(tokenData);
        fetchAndStoreUserDetails(language as ELanguage);

        // Initialize socket before navigation
        if (postSignupConfig) {
          initializeGlobalSocket(postSignupConfig, {
            onLogout: () => {
              // Handle logout if needed
              navigate(NAVIGATION_ROUTES.SIGNIN);
            },
            new_login: true, number: phoneNumber, otp: otp.join('')
          });
        }

        // CT event for new user: success
        handleClickCTEventSuccess();
        handleClickCTEventSignUpSuccessNewUser();
        localStorage.setItem('isLoggedIn', 'true');
        // Push CleverTap profile
        pushCleverTapProfile(phoneNumber as string, _id as string);
        
        navigate(postLoginRoute, {
          replace: true,
          state: autoWithdraw ? { autoWithdraw: true } : undefined,
        });
      } else if (authResponse?.success && authResponse?.flag === false && authResponse?.errorCode !== SIGN_IN_CODES.SUCCESS_CODE) {
        if (authResponse?.errorCode === SIGN_IN_CODES.EXISTING_USER_ERROR_CODE) {
          setTimeout(() => {
            setIsExistingUser(true);
            setOtp(Array(6).fill(''));
            // toast.error(authResponse?.error || t("invalidOtp"));
          }, 0);

          // CT event for existing user
          handleClickCTEventExistingUser();
          handleClickCTEventSignUpSuccessExistingUser();
          return;
        }
        setTimeout(() => {
          setOtp(Array(6).fill(''));
          toast.error(authResponse?.error || t("invalidOtp"));
        }, 0);

        // CT event for wrong OTP
        handleClickCTEventError();
      }
    } catch (error) {
      setTimeout(() => {
        setOtp(Array(6).fill(''));
        toast.error(t("errorOccurred"));
      }, 0);
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== '');

  useEffect(() => {
    if (isOtpComplete) {
      setTimeout(() => handleSubmit(new Event('submit') as unknown as React.FormEvent), 250);
    }
  }, [isOtpComplete]);


  useEffect(() => {
    const handleSms = (event: MessageEvent) => {
      if (event.data && event.data.type === 'sms' && event.data.message) {
        const otpMatch = event.data.message.match(/(\d{6})/);
        if (otpMatch) {
          setOtp(otpMatch[1].split(''));
        }
      }
    };

    window.addEventListener('message', handleSms);

    return () => {
      window.removeEventListener('message', handleSms);
      setIsExistingUser(false);
    };
  }, []);

  // Dismissing OTP returns to the landing page, which is the wallet.
  const handleByPass = useCallback(() => {
    setIsCrossLoading(true);
    setTimeout(() => {
      navigate(NAVIGATION_ROUTES.WALLET);
      setIsCrossLoading(false);
    }, 1000);
  }, [navigate]);

  if (isLoading || isCrossLoading) return <Loader transparent />;

  return (
    <div className={clsx(styles.otpContainer, "authPageContainer")}  >
      {!isExistingUser && (
        <div className={clsx(styles.contentBox, "authContentBox")} ref={authContentBoxRef}>
          <div className={clsx(styles.crossBtn)} onClick={handleByPass}>
            &#10005;
          </div>
          <div
            className={clsx(styles.pageHeading, "fontSizeMd", "fontWeightBold")}
          >
            {t("otp")}
          </div>
          <p className={clsx(styles.otpSentLabel, "fontSizeSm14")}>
            {t("enterSixDigitOTPSentTo")}
            <span className={styles.otpNo}> {phoneNumber}</span>
          </p>
          <form onSubmit={handleSubmit}>
            <div className={clsx(styles.otpBlock)}>
              {otp.map((digit, index) => (
                <input
                  className={clsx(styles.otpInput)}
                  key={index}
                  type="number"
                  maxLength={1}
                  inputMode="numeric"
                  value={digit}
                  onChange={(e) => handleInputChange(e.target.value, index)}
                  onKeyDown={(e) => handleBackspace(e, index)}
                  onPaste={(e) => handlePaste(e, index)}
                  ref={(el) => (inputRefOtp.current[index] = el)}
                  autoComplete="off"
                  pattern="\d*"
                  readOnly={false}
                  autoFocus={index === 0}
                />
              ))}
            </div>
            <p
              className={clsx(
                `${styles.resendOtp} ${isOtpSent && styles.disabled
                } fontSizeXs fontWeightBold `
              )}
              onClick={isOtpSent ? undefined : throttledResendOtp}
            >
              {isOtpSent
                ? `${t("retryIn")} ${countdown} ${t("sec")}`
                : t("resendOTP")}
            </p>
            <button
              type="submit"
              disabled={!isOtpComplete || isLoading}
              className={clsx(styles.otpBtn, "btn primaryBtn")}
              ref={buttonRef}
            >
              {t("continue")}
            </button>
          </form>
        </div>
      )}
      <ExistingZupeeUserModal
        isExistingUser={isExistingUser}
        setIsExistingUser={setIsExistingUser}
      />
    </div>
  );
};

export default OtpScreen;